import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, getUserRole, clearUserSession, getStoredUser, getAuthToken } from './api';
import { WorkflowProvider } from './context/WorkflowContext';
import { ThemeProvider } from './context/ThemeContext';
import { DataProvider, useData } from './context/DataContext';
import { DemoModeProvider, useDemoMode } from './context/DemoModeContext';
import Chat from './components/Chat';
import Toast from './components/Toast';
import CostCounter from './components/CostCounter';
import ThemeToggle from './components/ThemeToggle';
import DemoModeToggle from './components/DemoModeToggle';
import StudentOnboardingModal from './components/StudentOnboardingModal';
import AccessDenied from './components/AccessDenied';
import BugReportButton from './components/BugReportButton';
import DemoMatchmaker from './pages/DemoMatchmaker';
import Sessions from './pages/Sessions';
import StudentSessions from './pages/StudentSessions';
import StudentActivities from './pages/StudentActivities';
import StudentMessages from './pages/StudentMessages';
import SessionPlanner from './pages/SessionPlanner';
import Whiteboard from './pages/Whiteboard';
import GoogleMeetSync from './pages/GoogleMeetSync';
import StudentRoster from './pages/StudentRoster';
import AboutNeuropedagogy from './pages/AboutNeuropedagogy';
import DataFactory from './pages/DataFactory';
import AdminDashboard from './pages/AdminDashboard';
import AssessmentResults from './pages/AssessmentResults';
import OAuthCallback from './pages/OAuthCallback';
import './styles/lrbrain-theme.css';

const normalizeRole = (role) => {
  const normalized = String(role || '').trim().toLowerCase();
  if (normalized === 'brainiac') return 'student';
  return normalized;
};

const TEACHER_TAB_PERMISSIONS = {
  '/teacher': ['admin', 'team_captain'],
  '/teacher/sessions': ['admin', 'team_captain'],
  '/teacher/whiteboard': ['admin', 'team_captain'],
  '/teacher/session-planner': ['admin', 'team_captain'],
  '/teacher/student-roster': ['admin', 'team_captain'],
  '/teacher/demo': ['admin'],
  '/teacher/data-factory': ['admin'],
  '/teacher/google-meet-sync': ['admin', 'team_captain'],
  '/teacher/admin': ['admin'],
  '/about': ['admin', 'team_captain', 'student']
};

const canAccessRoute = (path, role) => {
  const normalizedRole = normalizeRole(role || getUserRole() || 'student');
  const allowed = TEACHER_TAB_PERMISSIONS[path] || [];
  return allowed.includes(normalizedRole);
};

const getRoleBadge = (role) => {
  const badges = {
    admin: 'Admin',
    team_captain: 'Team Captain',
    student: 'Student',
    brainiac: 'Student'
  };
  return badges[normalizeRole(role)] || null;
};

const getUserDisplayInfo = () => {
  const user = getStoredUser();
  if (user) {
    return {
      name: user.display_name || user.email?.split('@')[0] || 'User',
      role: normalizeRole(user.role),
      email: user.email
    };
  }
  const role = normalizeRole(getUserRole());
  if (role) {
    return { name: null, role, email: null };
  }
  return null;
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isStudentView, canSwitchView, getEffectiveRole } = useDemoMode();
  const effectiveRole = normalizeRole(getEffectiveRole());
  const userInfo = getUserDisplayInfo();
  const isAuthenticated = !!getAuthToken() || !!userInfo;
  const roleBadge = getRoleBadge(effectiveRole);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const isRoleStudent = effectiveRole === 'student';
  const activeView = isRoleStudent ? 'student' : (isStudentView ? 'student' : 'teacher');

  // Student onboarding modal
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  const checkOnboarding = useCallback(async () => {
    if (!isRoleStudent || onboardingChecked) return;
    setOnboardingChecked(true);
    try {
      const res = await apiFetch('/api/students/me');
      if (!res.ok) return;
      const data = await res.json();
      const complete = data.profile ? Boolean(data.profile.onboarding_complete) : false;
      if (!complete) setShowOnboarding(true);
    } catch {
      // silently ignore
    }
  }, [isRoleStudent, onboardingChecked]);

  useEffect(() => {
    if (isAuthenticated && isRoleStudent) checkOnboarding();
  }, [isAuthenticated, isRoleStudent, checkOnboarding]);
  const isTeacherShell = location.pathname.startsWith('/teacher');
  const isStudentShell = location.pathname.startsWith('/student');

  useEffect(() => {
    if (!isAuthenticated) return;

    if (location.pathname === '/') {
      navigate(isRoleStudent || activeView === 'student' ? '/student/sessions' : '/teacher', { replace: true });
      return;
    }

    if (isRoleStudent && isTeacherShell) {
      navigate('/student/sessions', { replace: true });
      return;
    }

    if (!isRoleStudent && activeView === 'student' && isTeacherShell) {
      navigate('/student/sessions', { replace: true });
      return;
    }

    if (!isRoleStudent && activeView === 'teacher' && isStudentShell) {
      navigate('/teacher', { replace: true });
    }
  }, [
    activeView,
    isAuthenticated,
    isRoleStudent,
    isStudentShell,
    isTeacherShell,
    location.pathname,
    navigate
  ]);

  const canAccess = (path) => canAccessRoute(path, effectiveRole);

  const isTeacherChatPage = location.pathname === '/teacher';
  const isStudentChatPage = location.pathname === '/student/chat';
  const isWhiteboardPage = location.pathname === '/teacher/whiteboard' || location.pathname === '/student/whiteboard';

  const [totalCost, setTotalCost] = useState(0);
  const [isCostCounterVisible, setIsCostCounterVisible] = useState(() => {
    const saved = localStorage.getItem('lrbrain-cost-counter-visible');
    return saved ? saved === 'true' : false;
  });

  const addCost = (cost) => {
    if (typeof cost === 'number' && cost > 0) {
      setTotalCost(prevCost => prevCost + cost);
    }
  };

  const resetCost = () => {
    setTotalCost(0);
  };

  const handleCostCounterToggle = (isVisible) => {
    setIsCostCounterVisible(isVisible);
  };

  const handleSignOut = () => {
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEV_AUTH_BYPASS === 'true') {
      try {
        sessionStorage.setItem('lrbrain-dev-auth-bypass-paused', 'true');
      } catch {
        // Ignore storage access issues in restrictive environments.
      }
    }
    clearUserSession();
    window.location.assign('/');
  };

  React.useEffect(() => {
    window.testCostUpdate = addCost;
    return () => {
      delete window.testCostUpdate;
    };
  }, []);

  React.useEffect(() => {
    const handler = (e) => {
      const cost = e?.detail?.cost;
      if (typeof cost === 'number' && cost > 0) {
        addCost(cost);
      }
    };
    window.addEventListener('lrbrain-cost-update', handler);
    return () => window.removeEventListener('lrbrain-cost-update', handler);
  }, []);

  React.useEffect(() => {
    let isSending = false;
    let lastSentAt = 0;
    const minIntervalMs = 10000;

    const sendAutoBug = async (payload) => {
      const now = Date.now();
      if (isSending || now - lastSentAt < minIntervalMs) return;
      isSending = true;
      lastSentAt = now;
      try {
        await apiFetch('/api/bugs/report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'frontend_auto',
            severity: 'high',
            title: payload.title || 'Frontend runtime error',
            description: payload.description || 'Unhandled frontend error',
            stack_trace: payload.stack,
            url: window.location.href,
            user_agent: navigator.userAgent,
            metadata: payload.metadata || {},
          }),
        });
      } catch {
        // Telemetry failures are intentionally ignored.
      } finally {
        isSending = false;
      }
    };

    const onError = (event) => {
      sendAutoBug({
        title: 'window.onerror',
        description: event?.message || 'Unhandled error event',
        stack: event?.error?.stack || null,
        metadata: {
          filename: event?.filename || null,
          lineno: event?.lineno || null,
          colno: event?.colno || null,
        },
      });
    };

    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      sendAutoBug({
        title: 'unhandledrejection',
        description: typeof reason === 'string' ? reason : 'Unhandled promise rejection',
        stack: reason?.stack || null,
        metadata: {
          reason_type: typeof reason,
        },
      });
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  React.useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const teacherNavItems = [
    { path: '/teacher', label: 'Chat', icon: 'chat', isVisible: true, isActive: location.pathname === '/teacher' },
    {
      path: '/teacher/student-roster',
      label: 'My Students',
      icon: 'students',
      isVisible: canAccess('/teacher/student-roster'),
      isActive: location.pathname === '/teacher/student-roster'
    },
    {
      path: '/teacher/google-meet-sync',
      label: 'Google',
      icon: 'meet',
      isVisible: canAccess('/teacher/google-meet-sync'),
      isActive: location.pathname === '/teacher/google-meet-sync'
    },
    {
      path: '/teacher/sessions',
      label: 'Sessions',
      icon: 'sessions',
      isVisible: true,
      isActive: location.pathname.startsWith('/teacher/sessions')
    },
    {
      path: '/teacher/session-planner',
      label: 'Session Planner',
      icon: 'planner',
      isVisible: canAccess('/teacher/session-planner'),
      isActive: location.pathname === '/teacher/session-planner'
    },
    {
      path: '/teacher/whiteboard',
      label: 'Whiteboard',
      icon: 'whiteboard',
      isVisible: true,
      isActive: location.pathname === '/teacher/whiteboard'
    },
    {
      path: '/teacher/admin',
      label: 'Admin',
      icon: 'admin',
      isVisible: canAccess('/teacher/admin'),
      isActive: location.pathname === '/teacher/admin'
    }
  ];

  const studentNavItems = [
    {
      path: '/student/sessions',
      label: 'Sessions',
      icon: 'sessions',
      isVisible: true,
      isActive: location.pathname === '/student/sessions'
    },
    {
      path: '/student/google-meet-sync',
      label: 'Google',
      icon: 'meet',
      isVisible: true,
      isActive: location.pathname === '/student/google-meet-sync'
    },
    {
      path: '/student/chat',
      label: 'Chat',
      icon: 'chat',
      isVisible: true,
      isActive: location.pathname === '/student/chat'
    },
    {
      path: '/student/activities',
      label: 'Activities',
      icon: 'activities',
      isVisible: true,
      isActive: location.pathname === '/student/activities'
    },
    {
      path: '/student/messages',
      label: 'Notifications',
      icon: 'messages',
      isVisible: true,
      isActive: location.pathname === '/student/messages'
    },
    {
      path: '/student/whiteboard',
      label: 'Whiteboard',
      icon: 'whiteboard',
      isVisible: true,
      isActive: location.pathname === '/student/whiteboard'
    }
  ];

  const navItems = activeView === 'student' ? studentNavItems : teacherNavItems;

  const renderNavIcon = (icon) => {
    switch (icon) {
      case 'chat':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 5h16v10H7l-3 3V5z" />
          </svg>
        );
      case 'students':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="8" cy="8" r="3" />
            <circle cx="16" cy="7" r="2.5" />
            <path d="M3 19c0-3 2.4-5 5-5s5 2 5 5" />
            <path d="M13 19c0-2.2 1.8-3.8 3.8-3.8S21 16.8 21 19" />
          </svg>
        );
      case 'sessions':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="4" y="5" width="16" height="15" rx="2" />
            <path d="M8 3v4M16 3v4M4 10h16" />
          </svg>
        );
      case 'planner':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="5" y="3" width="14" height="18" rx="2" />
            <path d="M8 8h8M8 12h8M8 16h5" />
          </svg>
        );
      case 'meet':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="6" width="13" height="12" rx="2" />
            <path d="M16 10l5-3v10l-5-3z" />
          </svg>
        );
      case 'whiteboard':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="4" width="18" height="12" rx="2" />
            <path d="M9 20h6M12 16v4" />
          </svg>
        );
      case 'admin':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3l2.4 3.8 4.4 1.2-2.8 3.6.2 4.6L12 14.8 7.8 16.2l.2-4.6L5.2 8l4.4-1.2z" />
          </svg>
        );
      case 'activities':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 4h10v4H7zM5 10h14v10H5z" />
          </svg>
        );
      case 'messages':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 5h16v11H8l-4 4V5z" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
          </svg>
        );
    }
  };

  const handleSidebarToggle = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1100px)').matches) {
      setIsSidebarOpen(prev => !prev);
      return;
    }
    setIsSidebarCollapsed(prev => !prev);
  };

  if (location.pathname.startsWith('/share/whiteboard/')) {
    return (
      <div className="App">
        <Routes>
          <Route path="/share/whiteboard/:token" element={<Whiteboard publicShare />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toast />
      </div>
    );
  }

  return (
    <div className={`App app-shell ${isSidebarOpen ? 'sidebar-open' : ''} ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className="lrbrain-sidebar" aria-label="Primary navigation">
        <div className="lrbrain-sidebar-brand">
          <Link to={activeView === 'student' ? '/student/sessions' : '/teacher'} className="lrbrain-sidebar-brand-link">
            <img src="/lrbrain-logo.png" alt="LRB.ai logo" className="lrbrain-logo" />
            <div>
              <p className="lrbrain-sidebar-brand-title">Academic Portal</p>
              <p className="lrbrain-sidebar-brand-subtitle">L.R.Brain AI</p>
            </div>
          </Link>
          <button
            type="button"
            className="lrbrain-sidebar-close"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close navigation"
          >
            x
          </button>
        </div>

        <nav className="lrbrain-sidebar-nav">
          {navItems.filter(item => item.isVisible).map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={item.isActive ? 'active' : ''}
              title={isSidebarCollapsed ? item.label : ''}
            >
              <span className="lrbrain-sidebar-icon">{renderNavIcon(item.icon)}</span>
              <span className="lrbrain-sidebar-nav-label">{item.label}</span>
            </Link>
          ))}
        </nav>
      </aside>

      <div
        className="lrbrain-sidebar-backdrop"
        onClick={() => setIsSidebarOpen(false)}
        aria-hidden="true"
      />

      <div className="lrbrain-shell-main">
        <header className="lrbrain-utility-strip">
          <button
            type="button"
            className="lrbrain-sidebar-toggle"
            onClick={handleSidebarToggle}
            aria-label="Toggle navigation"
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 6h16M4 12h16M4 18h16" />
                <path d="M9 8l-3 4l3 4" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 6h16M4 12h16M4 18h16" />
                <path d="M15 8l3 4l-3 4" />
              </svg>
            )}
          </button>

          <div className="lrbrain-utility-right">
            {isAuthenticated && (
              <div className="lrbrain-utility-primary">
                {userInfo?.name && (
                  <div className="lrbrain-user-name lrbrain-user-name--shell" title={userInfo.email || ''}>
                    {userInfo.name}
                  </div>
                )}
                {roleBadge && (
                  <div className="lrbrain-role-badge lrbrain-role-badge--shell">
                    {roleBadge}
                    {canSwitchView && isStudentView && <span className="demo-indicator">(Student View)</span>}
                  </div>
                )}
                <button
                  className="lrbrain-btn lrbrain-btn-outline lrbrain-btn-shell lrbrain-logout-btn"
                  onClick={handleSignOut}
                  aria-label="Log out"
                  title="Log out"
                >
                  <span className="lrbrain-logout-label-full">Sign Out</span>
                  <span className="lrbrain-logout-label-compact">Logout</span>
                </button>
              </div>
            )}
            <div className="lrbrain-utility-secondary">
              {isAuthenticated && <DemoModeToggle />}
              <ThemeToggle compact={true} />
              <CostCounter
                totalCost={totalCost}
                isVisible={isCostCounterVisible}
                onToggle={handleCostCounterToggle}
                compact={true}
              />
              <Link to="/about" className="lrbrain-about-link lrbrain-about-link--shell" title="About Neuropedagogy">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
              </Link>
            </div>
          </div>

          <div className={`lrbrain-mobile-action-cluster ${isWhiteboardPage ? 'whiteboard-active' : ''}`}>
            <button
              type="button"
              className="mobile-global-nav-btn"
              onClick={handleSidebarToggle}
              aria-label="Open site navigation"
              title="Open site navigation"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </header>

        <main className="lrbrain-main scrollable">
          <Routes>
            <Route
              path="/teacher"
              element={(activeView === 'teacher' && !isRoleStudent)
                ? <Chat onCostUpdate={addCost} onCostReset={resetCost} />
                : <AccessDenied requiredRoles={['admin', 'team_captain']} />}
            />
            <Route
              path="/teacher/sessions"
              element={(activeView === 'teacher' && !isRoleStudent)
                ? <Sessions />
                : <AccessDenied requiredRoles={['admin', 'team_captain']} />}
            />
            <Route
              path="/teacher/session-planner"
              element={(activeView === 'teacher' && !isRoleStudent && canAccess('/teacher/session-planner'))
                ? <SessionPlanner />
                : <AccessDenied requiredRoles={['admin', 'team_captain']} />}
            />
            <Route
              path="/teacher/student-roster"
              element={(activeView === 'teacher' && !isRoleStudent && canAccess('/teacher/student-roster'))
                ? <StudentRoster />
                : <AccessDenied requiredRoles={['admin', 'team_captain']} />}
            />
            <Route
              path="/teacher/demo"
              element={(activeView === 'teacher' && !isRoleStudent && canAccess('/teacher/demo'))
                ? <DemoMatchmaker />
                : <AccessDenied requiredRoles={['admin']} />}
            />
            <Route
              path="/teacher/data-factory"
              element={(activeView === 'teacher' && !isRoleStudent && canAccess('/teacher/data-factory'))
                ? <DataFactory />
                : <AccessDenied requiredRoles={['admin']} />}
            />
            <Route
              path="/teacher/admin"
              element={(activeView === 'teacher' && !isRoleStudent && canAccess('/teacher/admin'))
                ? <AdminDashboard />
                : <AccessDenied requiredRoles={['admin']} />}
            />
            <Route
              path="/teacher/whiteboard"
              element={(activeView === 'teacher' && !isRoleStudent)
                ? <Whiteboard />
                : <AccessDenied requiredRoles={['admin', 'team_captain']} />}
            />
            <Route
              path="/teacher/google-meet-sync"
              element={(activeView === 'teacher' && !isRoleStudent && canAccess('/teacher/google-meet-sync'))
                ? <GoogleMeetSync />
                : <AccessDenied requiredRoles={['admin', 'team_captain']} />}
            />

            <Route
              path="/student/sessions"
              element={(activeView === 'student' || isRoleStudent) ? <StudentSessions /> : <AccessDenied requiredRoles={['student']} />}
            />
            <Route
              path="/student/google-meet-sync"
              element={(activeView === 'student' || isRoleStudent) ? <GoogleMeetSync studentMode /> : <AccessDenied requiredRoles={['student']} />}
            />
            <Route
              path="/student/chat"
              element={(activeView === 'student' || isRoleStudent)
                ? <Chat onCostUpdate={addCost} onCostReset={resetCost} />
                : <AccessDenied requiredRoles={['student']} />}
            />
            <Route
              path="/student/activities"
              element={(activeView === 'student' || isRoleStudent) ? <StudentActivities /> : <AccessDenied requiredRoles={['student']} />}
            />
            <Route
              path="/student/messages"
              element={(activeView === 'student' || isRoleStudent) ? <StudentMessages /> : <AccessDenied requiredRoles={['student']} />}
            />
            <Route
              path="/student/whiteboard"
              element={(activeView === 'student' || isRoleStudent) ? <Whiteboard /> : <AccessDenied requiredRoles={['student']} />}
            />

            <Route path="/about" element={<AboutNeuropedagogy />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/results/:token" element={<AssessmentResults />} />
            <Route path="/chat" element={<Navigate to="/teacher" replace />} />
            <Route path="/sessions" element={<Navigate to="/teacher/sessions" replace />} />
            <Route path="/session-planner" element={<Navigate to="/teacher/session-planner" replace />} />
            <Route path="/student-roster" element={<Navigate to="/teacher/student-roster" replace />} />
            <Route path="/whiteboard" element={<Navigate to="/teacher/whiteboard" replace />} />
            <Route path="/google-meet-sync" element={<Navigate to={`/teacher/google-meet-sync${location.search || ''}`} replace />} />
            <Route path="/admin" element={<Navigate to="/teacher/admin" replace />} />
            <Route path="/demo" element={<Navigate to="/teacher/demo" replace />} />
            <Route path="/data-factory" element={<Navigate to="/teacher/data-factory" replace />} />
            <Route
              path="/"
              element={<Navigate to={isRoleStudent || activeView === 'student' ? '/student/sessions' : '/teacher'} replace />}
            />
            <Route
              path="*"
              element={<Navigate to={isRoleStudent || activeView === 'student' ? '/student/sessions' : '/teacher'} replace />}
            />
          </Routes>
        </main>
      </div>

      <BugReportButton isChatPage={isTeacherChatPage || isStudentChatPage} />
      <Toast />

      {showOnboarding && (
        <StudentOnboardingModal
          onComplete={() => setShowOnboarding(false)}
          onSkip={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <WorkflowProvider>
      <ThemeProvider>
        <DemoModeProvider>
          <DataProvider>
            <DataPrefetcher />
            <AppContent />
          </DataProvider>
        </DemoModeProvider>
      </ThemeProvider>
    </WorkflowProvider>
  );
}

function DataPrefetcher() {
  const { prefetch } = useData();
  const location = useLocation();
  const { getEffectiveRole, isStudentView } = useDemoMode();
  const effectiveRole = normalizeRole(getEffectiveRole());
  const isAuthenticated = !!effectiveRole;
  const canAccessStudents = (effectiveRole === 'admin' || effectiveRole === 'team_captain') && !isStudentView;

  const prefetchedRef = React.useRef({ auth: false, routes: new Set() });

  useEffect(() => {
    if (isAuthenticated && canAccessStudents && !prefetchedRef.current.auth) {
      prefetchedRef.current.auth = true;
      prefetch(['students']);
    }
  }, [isAuthenticated, canAccessStudents, prefetch]);

  useEffect(() => {
    if (!isAuthenticated || !canAccessStudents) return;

    const currentPath = location.pathname;
    if (prefetchedRef.current.routes.has(currentPath)) return;

    if (currentPath === '/teacher/student-roster') {
      prefetchedRef.current.routes.add(currentPath);
    } else if (currentPath === '/teacher/session-planner') {
      prefetchedRef.current.routes.add(currentPath);
      prefetch(['students', 'plannerData']);
    }
  }, [location.pathname, isAuthenticated, canAccessStudents, prefetch]);

  return null;
}

export default App;
